<template>
  <div class="position-relative">
    <!-- Results & Filter Bar -->
    <div
      v-if="!searching"
      class="sticky-top bg-white p-2"
      style="border-bottom: 1px solid lightgray; min-height: 3.2rem;">
      <div class="row">
        <div
          class="col-md-12"
          style="z-index:1; padding: 0px 15px;">
          <!-- Result Label -->
          <div
            v-if="!searching && products"
            class="float-start text-muted">
            <template class="d-none d-md-block">
              <!-- Search -->
              <template v-if="query.query">
                <span>Result(s) for:</span>
                <span class="mx-1">'{{ query.query }}'</span>
              </template>
              <!-- Category Browse -->
              <template v-if="query.category">
                <span>Browsing:</span>
                <span class="ms-1">{{ query.category }}</span>
                <span v-if="query.sub">
                  >
                  {{ query.sub }}
                </span>
              </template>
              <!-- Brand Browse -->
              <template v-if="query.brand">
                <a
                  class="btn btn-primary btn-sm me-1"
                  href="#"
                  @click="backToBrands">
                  <i class="fa fa-chevron-left"/>
                  Back to Brands
                </a>
                <span>Browsing:</span>
                <span class="ms-1">{{ query.brand }}</span>
              </template>
              <!-- Clearance -->
              <template v-if="query.action == 'clearance' && !query.category">
                <span>Browsing: Clearance Items</span>
              </template>
              <!-- New Products -->
              <template v-if="['new', 'virtualShowNew'].includes(query.action)">
                <span>Browsing: New Products</span>
              </template>
              <template v-if="query.action == 'virtualShowHot'">
                <span>Browsing: Hot Buys</span>
              </template>
              <!-- Browse Promo -->
              <template v-if="query.action == 'browsePromo'">
                <span>Browsing Promo: '{{ activePromo.length ? activePromo[0].promo_title : null}}'</span>
              </template>
              <template v-if="['reorderPad', 'virtualShowReorderPad'].includes(query.action)">
                <span>Browsing: Reorder Pad</span>
              </template>
              <template v-if="query.action == 'virtualShowFavorites'">
                <span>Your Favorites</span>
              </template>
              <template v-if="query.action == 'virtualShowDropShips'">
                <span>Drop Shipments</span>
              </template>
              <!-- Product Count -->
              <span class="ms-1">({{ numeral(products.length).format('0,0') }})</span>
            </template>
            <router-link
              v-if="browsing"
              class="btn btn-sm btn-outline-primary"
              to="/browse">
                <i class="fa fa-caret-left"/>
                Categories
            </router-link>
            <!-- Promo PDF Download -->
            <div
              v-if="activePromo.length"
              class="download-left">
              <!-- Dropdown: mobile, or multiple assets -->
              <div
                v-if="isMobile || activePromo.length > 1"
                class="dropdown">
                <button
                  class="btn btn-primary btn-sm dropdown-toggle"
                  type="button"
                  id="promoDownloadDropdown"
                  data-bs-toggle="dropdown">
                  <i class="fa fa-download"/>
                  Downloads
                </button>
                <div
                  class="dropdown-menu"
                  aria-labelledby="promoDownloadDropdown">
                  <template
                    v-for="asset in activePromo"
                    :key="asset.id">
                    <a
                      v-if="asset.pdf_url"
                      class="dropdown-item"
                      :href="asset.pdf_url"
                      target="_blank">
                      Download PDF{{ activePromo.length > 1 ? ` (${asset.promo_title})` : '' }}
                    </a>
                  </template>
                </div>
              </div>
              <!-- Single button: desktop, single asset -->
              <template
                v-if="!isMobile && activePromo.length == 1"
                v-for="asset in activePromo"
                :key="asset.id">
                <a
                  v-if="asset.pdf_url"
                  class="btn btn-primary btn-sm me-1 mb-1"
                  :href="asset.pdf_url"
                  target="_blank">
                  <i class="fa fa-download"/>
                  Download PDF{{ activePromo.length > 1 ? ` (${asset.promo_title})` : '' }}
                </a>
              </template>
            </div>
          </div>
          <FilterControls
            v-if="!searching"
            class="float-end"
            :products="products"
            :loading="searching"
            @subSearch="subSearch"/>
        </div>
      </div>
    </div>
    <!-- Results -->
    <div
      v-if="products && !searching && productView == 'grid' && !isMobile"
      class="container-fluid">
      <SuggestBanner/>
      <!-- Gird View -->
      <div class="row animate__animated animate__fadeIn animate__faster">
        <div
          v-for="(p, i) in windowedProducts"
          :key="p.product_id"
          class="col-xxl-2 col-xl-3 col-md-4 col-sm-6 d-none d-sm-block my-2">
          <DataLoader
            :index="i"
            :key="i + dataLoaderKey"
            :loadInterval="loadInterval"
            :id="'dataLoader' + '-' + 'card' + '-'+ i"
            @getData="getData"/>
          <ProductCard
            :key="p.product_id"
            :loadImages="loadImages"
            :virtualShow="virtualShow"
            :product="p"/>
        </div>
        <div
          v-if="renderCount < products.length"
          ref="gridSentinel"
          class="col-12 d-flex justify-content-center py-4">
          <Spinner/>
        </div>
      </div>
    </div>
    <!-- List View -->
    <div
      v-if="products && !searching && productView == 'list' && !isMobile"
      class="row animate__animated animate__fadeIn animate__faster">
      <div
        v-for="(p, i) in windowedProducts"
        :key="p.product_id"
        class="col-12 d-none d-sm-block">
        <DataLoader
          :index="i"
          :key="i + dataLoaderKey"
          :loadInterval="loadInterval"
          :id="'dataLoader' + '-' + 'list' + '-'+ i"
          @getData="getData"/>
        <ProductList
          :key="p.product_id"
          :virtualShow="virtualShow"
          :loadImages="loadImages"
          :product="p"/>
      </div>
      <div
        v-if="renderCount < products.length"
        ref="listSentinel"
        class="col-12 d-flex justify-content-center py-4">
        <Spinner/>
      </div>
    </div>
    <!-- Mobile View -->
    <div
      v-if="!searching && isMobile"
      class="row animate__animated animate__fadeIn animate__faster p-2">
      <div
        v-for="(p, i) in windowedProducts"
        :key="p.product_id"
        class="col-12 d-block d-sm-none">
        <DataLoader
          :index="i"
          :key="i + dataLoaderKey"
          :loadInterval="loadInterval"
          :id="'dataLoader' + '-' + 'mobile' + '-'+ i"
          @getData="getData"/>
        <ProductMobile
          :key="p.product_id"
          :loadImages="loadImages"
          :virtualShow="virtualShow"
          :product="p"/>
      </div>
      <div
        v-if="renderCount < products.length"
        ref="mobileSentinel"
        class="col-12 d-flex justify-content-center py-4 d-block d-sm-none">
        <Spinner/>
      </div>
    </div>
    <div
      v-if="products && !products.length && !searching"
      class="row">
      <div class="col-md-12 text-center">
        <h4 class="text-muted mt-5">No Results</h4>
      </div>
    </div>
    <div
      v-if="searching"
      class="row">
      <div class="col-md-12 text-center mt-5">
        <Spinner/>
      </div>
    </div>
    <MobileFilterButton
      v-if="!searching"
      class="float-end mx-1 d-block d-sm-none"
      :loading="searching"
      :virtualShow="virtualShow"/>
  </div>
</template>
<script>
  import ProductCard from './ProductCard.vue'
  import ProductList from './ProductList.vue'
  import ProductMobile from './ProductMobile.vue'
  import DataLoader from './DataLoader.vue'
  import Spinner from '../../../global/Spinner.vue';
  import MobileFilterButton from '../../menus/mobile/filters/MobileFilterButton.vue'
  import FilterControls from '../../menus/views/FilterControls/FilterControls.vue'
  import {processFilters} from '../../../../repositories/filterActions'
  import SuggestBanner from '../customerSuggestions/SuggestBanner.vue';

  export default {
    props: ['virtualShow'],
    data(){
      return {
        loadInterval: 24,
        renderBatch: 48,
        renderCount: 48,
        subSearchTerm: null,
        loadImages: [],
        screenWidth: window.innerWidth
      }
    },
    created(){
      this.getProducts()
      this.updateScreenWidth = _.debounce(this.updateScreenWidth, 150)
    },
    components:{
      ProductCard,
      ProductList,
      ProductMobile,
      DataLoader,
      Spinner,
      MobileFilterButton,
      FilterControls,
      SuggestBanner
    },
    mounted(){
      this.loadMoreObserver = new IntersectionObserver(this.handleLoadMoreIntersection)
      this.observeLoadMore()
      window.addEventListener('resize', this.updateScreenWidth)
    },
    updated(){
      this.observeLoadMore()
    },
    unmounted(){
      if(this.loadMoreObserver) this.loadMoreObserver.disconnect()
      window.removeEventListener('resize', this.updateScreenWidth)
    },
    watch:{
      products(){
        this.renderCount = this.renderBatch
      },
      meritSupply(newVal){
        const meritUser = this.$store.getters.sessionCompany == 'Merit Supply'
        if(meritUser && newVal){
          this.getProducts()
        }
      }
    },
    methods:{
      updateScreenWidth(){
        this.screenWidth = window.innerWidth
      },
      observeLoadMore(){
        const sentinels = [this.$refs.gridSentinel, this.$refs.listSentinel, this.$refs.mobileSentinel].filter(Boolean)
        if(!sentinels.length) return
        this.loadMoreObserver.disconnect()
        sentinels.forEach(el => this.loadMoreObserver.observe(el))
      },
      handleLoadMoreIntersection(entries){
        entries.forEach(entry => {
          if(entry.isIntersecting){
            this.renderCount += this.renderBatch
          }
        })
      },
      getProducts(){
        const route = this.$route
        const params = {
          path: route.path,
          query: route.query,
          meta: {
            scan: route.query.scan,
            barcode: route.query.query,
            barcodeFormat: route.query.barcodeFormat,
            barcodeFormatString: route.query.barcodeFormatString
          }
        }
        this.$store.dispatch('getSearchProducts', params)
      },
      // eclipse product data fetch
      getData(index){
        let products
        if(index == 0){
          products = _.slice(this.products, index, this.loadInterval)
        }else{
          products = _.slice(this.products, index + 1, this.loadInterval + (index + 1))
        }
        products = _.map(products, 'product_id')
        if(products?.length){
          this.loadImages = [...this.loadImages, ...products]
          this.$store.dispatch('getProductData', {route: this.route.path, products})
        }
      },
      subSearch(query){
        this.subSearchTerm = query
      },
      backToBrands(){
        if(this.virtualShow){
          this.$router.push({name: 'VirtualShow'})
          this.$store.dispatch('vShowSetView', 'mfg')
        }else{
          this.$router.push({name: 'BrowseBrands'})
        }
      }
    },
    computed:{
      products(){
        const base = processFilters()
        // SubSearch Logic
        if(this.subSearchTerm){
          const results = _.filter(base, function(p){
            return _.every(this.subSearchTerm.split(' '), s => p.description.toLowerCase().includes(s.toLowerCase()))
          }.bind(this))
          return results
        }
        return base
      },
      windowedProducts(){return this.products.slice(0, this.renderCount)},
      isMobile(){return this.screenWidth < 576},
      productView(){return this.$store.getters.productView},
      searching(){return this.$store.getters.productLoadState},
      route(){return this.$router.currentRoute.value},
      query(){return this.route.query},
      reorderPad(){return this.query?.action == 'reorderPad'},
      windowSize(){return window.innerHeight + window.innerWidth},
      activePromo(){return this.$store.getters.productResults.promo_assets || []},
      dataLoaderKey(){
        return ('-' + this.products.length + '-' + this.productView + '-' + this.windowSize).toString()
      },
      browsing(){
        if(this.query?.action){
          return this.query.action.includes('browseMain') || this.query.action.includes('browseSub')
        }
        return false
      },
      meritSupply(){return this.$store.getters.meritSupply}
    }
  }
</script>

<style scoped>
  .download-left{
    display: flex;
    flex-wrap: wrap;
  }
  @media screen and (max-width: 575px){
    .download-left{
      margin-left: 3rem;
    }
  }
</style>